import React, { useEffect, useState } from "react";
import { Button, Checkbox, Dropdown, Space, Divider, theme } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import { TableColumnType } from "antd";
import { AnyObject } from "antd/es/_util/type";

const { useToken } = theme;

interface TableColumnSelectorProps {
  columns: TableColumnType<AnyObject>[];
  onChange: (visibleColumns: string[]) => void;
  storageKey: string;
  defaultHiddenColumns?: string[];
}

export const TableColumnSelector: React.FC<TableColumnSelectorProps> = ({
  columns,
  onChange,
  storageKey,
  defaultHiddenColumns = [],
}) => {
  const { token } = useToken();

  const getColumnOptions = () => {
    return columns
      .filter((col) => col.dataIndex && col.title && col.fixed !== "right") // Exclude action columns
      .map((col) => ({
        label: col.title as string,
        value: col.dataIndex as string,
      }));
  };

  const columnOptions = getColumnOptions();
  const allColumnKeys = columnOptions.map((opt) => opt.value);

  const getInitialVisible = (): string[] => {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as string[];
        // Filter to only include columns that still exist
        const existingVisible = parsed.filter((key: string) => allColumnKeys.includes(key));
        // Add any NEW columns that weren't in storage (unless they're default hidden)
        const newColumns = allColumnKeys.filter(
          (key) => !parsed.includes(key) && !defaultHiddenColumns.includes(key)
        );
        return [...existingVisible, ...newColumns];
      } catch {
        return allColumnKeys.filter((key) => !defaultHiddenColumns.includes(key));
      }
    }
    // First time: show all except default hidden
    return allColumnKeys.filter((key) => !defaultHiddenColumns.includes(key));
  };

  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    getInitialVisible()
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(visibleColumns));
    onChange(visibleColumns);
  }, [visibleColumns, storageKey, onChange]);

  const handleSelectAll = () => {
    setVisibleColumns(allColumnKeys);
  };

  const handleDeselectAll = () => {
    setVisibleColumns([]);
  };

  const dropdownContent = (
    <div
      style={{
        padding: "12px",
        minWidth: "250px",
        maxHeight: "400px",
        overflowY: "auto",
        backgroundColor: token.colorBgElevated,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadowSecondary,
      }}
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Button size="small" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button size="small" onClick={handleDeselectAll}>
            Deselect All
          </Button>
        </Space>
        <Divider style={{ margin: "8px 0" }} />
        <Checkbox.Group
          options={columnOptions}
          value={visibleColumns}
          onChange={(values) => setVisibleColumns(values as string[])}
          style={{ display: "flex", flexDirection: "column", gap: "8px" }}
        />
      </Space>
    </div>
  );

  return (
    <Dropdown
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      dropdownRender={() => dropdownContent}
      trigger={["click"]}
      placement="bottomRight"
      overlayStyle={{ padding: 0 }}
    >
      <Button icon={<SettingOutlined />}>Columns</Button>
    </Dropdown>
  );
};
